<template>
  <div class="wrap">
    <header>
      <h1>悬浮目录框</h1>
      <div class="sub">文章目录模块：从 linux.sb 主题页实现还原，含滚动高亮、收起、悬浮停靠，以及实测发现的真实缺陷</div>
      <div class="meta">
        <span class="chip">来源：linux.sb/topic/23323「文章目录」卡片</span>
        <span class="chip">原实现：plugins.js（纯前端动态生成）</span>
        <span class="chip">含运行时实测：633×1265 视口</span>
        <span class="chip">整理时间：2026-09</span>
      </div>
    </header>

    <!-- ========== 0 开场 ========== -->
    <div class="note blue">
      <p>这个模块实现的是论坛主题页右上角那张「<b>文章目录</b>」卡片：它不依赖服务端渲染，而是页面加载后<b>扫描楼主第一帖的正文标题</b>，动态生成目录、自动生成锚点、滚动时高亮当前阅读位置，并且可以在滚动过程中<b>悬浮固定在侧栏</b>。</p>
      <p>原站点把这段逻辑写在一个自研插件里（源码注释块 <code>/* topic_outline */</code>，入口 <code>topic_outline_main()</code>），本模块把它 Vue 化后复刻在此页面中，下面所有演示都是<b>真实运行的效果</b>。</p>
    </div>
    <div class="note warn">
      <b>先说一个实测发现：</b>原站点上「<b>滚动时自动高亮</b>」这条链路<b>实际是失效的</b>，而「点击跳转」正常。<br>
      根因不是参数没调好，而是正文被另一个模块的 <code>overflow:hidden</code> 容器裁剪了 —— <b>无论 <code>rootMargin</code> 怎么调都无效</b>。完整实测验尸见<b>第 4 节</b>。
    </div>

    <!-- ========== 1 先看效果 ========== -->
    <h2><span class="num">1</span>先看效果：真实运行的目录卡片</h2>
    <p class="lead">下面左侧是「文章正文」，右侧是「悬浮目录框」——就是本模块的实现。它完全按照原始模块的行为工作：</p>

    <div class="demo-bar">
      <div class="demo-tip">
        <b>试试这些操作：</b>
        <span>① 滚动页面看目录高亮跟随</span>
        <span>② 点击目录项跳转</span>
        <span>③ 点右上角「收起 / 展开」</span>
      </div>
      <div class="demo-stat">
        <span class="stat-pill">已识别标题：<b>{{ headingCount }}</b> 个</span>
        <span class="stat-pill">当前章节：<b>{{ activeText }}</b></span>
        <span class="stat-pill" :class="{ on: docked }">悬浮停靠：<b>{{ docked ? '已停靠' : '跟随侧栏' }}</b></span>
      </div>
    </div>

    <div class="demo-stage">
      <!-- 演示正文：目录的扫描源 -->
      <article ref="article" class="demo-article">
        <div class="demo-article-head">
          <div class="demo-article-title">演示文章：一次把「文章目录」讲清楚</div>
          <div class="demo-article-sub">下面这些标题就是目录的扫描来源，h2 / h3 / h4 分别对应三级缩进</div>
        </div>

        <h2>为什么需要文章目录</h2>
        <p>长帖最容易劝退读者的地方，是「不知道该看哪一段」。读者滚了两屏，才发现自己关心的内容在最底部；想回到刚才看过的位置，又只能凭记忆翻。</p>
        <p>目录卡片解决的正是这个问题：把文章的骨架裸露在侧栏上，让读者随时知道「现在读到哪、还剩多少、能不能直接跳过去」。</p>
        <h3>长帖的三个痛点</h3>
        <p>定位难、回看难、进度不明确。前两个是导航问题，最后一个是心理问题——知道还剩多少，读者才愿意继续读下去。</p>
        <h4>定位难</h4>
        <p>标题层级一多，浏览器的原生查找就力不从心了。目录提供的是一条「结构化」的入口，比 Ctrl+F 更符合人的阅读直觉。</p>
        <h4>回看难</h4>
        <p>读到一半被打断，回来时往往忘了位置。目录里的高亮项就是「你刚才读到哪」的书签。</p>

        <h2>它到底是怎么实现的</h2>
        <p>核心只有四步：<b>扫描标题 → 生成锚点 → 渲染列表 → 监听滚动</b>。每一步都有一些容易被忽略的细节。</p>
        <h3>扫描标题：只扫楼主第一帖</h3>
        <p>原实现的选择器是 <code>.topic-post-list .post-entry:first-child .post-content</code>，只取<b>第一帖</b>的正文。这是关键：回帖里同样会有 h2/h3，如果连回帖一起扫，目录就会被别人的回复污染。</p>
        <div class="note warn">
          <b>⚠️ 另一个克制点：</b>标题数量少于 <b>2 个</b>时<b>完全不渲染</b>。短帖本来就不需要目录，凭空多出一张卡片反而占地方。
        </div>
        <h3>生成锚点：Unicode 感知的 slug</h3>
        <p>每个标题如果没有 id，就按标题文本生成一个可读的锚点，前缀固定 <code>topic-outline-</code>，冲突时递增后缀。</p>
        <h4>中文标题不会被清空</h4>
        <p>关键在于正则用了 <code>\p{L}\p{N}</code> 加 <code>u</code> 标志：<code>效果</code> 会直接保留成 <code>效果</code>，而不是被替换成空、退化成 <code>section-1</code> 这种机器味很重的命名。</p>
        <h4>不覆盖已有 id</h4>
        <p>如果标题本来就有 id，一律尊重原值——否则会破坏页面里其它依赖该 id 的链接。</p>

        <h2>滚动高亮为什么用 IntersectionObserver</h2>
        <p>最直觉的做法是监听 <code>scroll</code> 事件，然后算每个标题的位置。但滚动事件触发极其频繁，为了不卡顿就必须节流，节流又会带来延迟感。</p>
        <h3>把视口压成一条窄带</h3>
        <p>原实现用的是 <code>rootMargin: '-10% 0px -75% 0px'</code>：它把「有效视口」压缩成屏幕上大约 15% 高度的一条横带。标题进入这条带才算「当前项」，这比「标题出现在视口里」精确得多。</p>
        <div class="flow">
          <div class="step">
            <div class="q">不用 scroll 事件</div>
            <div class="a">交给浏览器在合成线程判断，主线程没有滚动性能负担，也不需要手写节流。</div>
          </div>
          <div class="step">
            <div class="q">多个同时命中怎么办</div>
            <div class="a">取 <code>boundingClientRect.top</code> 最小的那个，也就是最靠上的标题，符合「从上往下读」的直觉。</div>
          </div>
          <div class="step">
            <div class="q">不支持怎么办</div>
            <div class="a">先用 <code>'IntersectionObserver' in window</code> 做特性检测，不支持就只保留静态目录，绝不报错。</div>
          </div>
        </div>
        <div class="note warn">
          <b>⚠️ 但这里有个反直觉的事实：</b>这套设计在原站点上<b>实测是失效的</b>——滚动时高亮从不切换。原因不在 <code>rootMargin</code>，而在正文被另一个模块的 <code>overflow:hidden</code> 容器裁剪了。<b>详见第 4 节的实测验尸。</b>
        </div>

        <h2>点击跳转与吸顶补偿</h2>
        <p>如果站点的顶部导航是固定的，直接用浏览器原生锚点跳转，目标标题会被压在导航栏底下——读者点完反而看不到标题。</p>
        <h3>两套补偿机制</h3>
        <p>一是给每个标题设置 <code>scrollMarginTop</code>，让原生锚点跳转（比如从 URL 带 hash 进页面）也不被遮挡；二是点击目录项时手动算滚动位置，不依赖原生跳转。</p>
        <h4>尊重减弱动效</h4>
        <p>检测到 <code>prefers-reduced-motion: reduce</code> 时，滚动的 <code>behavior</code> 从 <code>smooth</code> 降级为 <code>auto</code>，直接跳过去，不做动画。</p>

        <h2>悬浮停靠：原卡片只是占位符</h2>
        <p>「滚动时目录一直贴在右上角」这件事，实现方式比看起来要绕：<b>真正固定显示的不是原卡片，而是它的克隆体</b>。</p>
        <h3>克隆 + 占位</h3>
        <p>原卡片被设成 <code>visibility: hidden</code> 并撑成克隆体的高度作为占位（防止页面跳动），克隆体则被塞进停靠容器、用 <code>position: fixed</code> 常驻显示。</p>
        <h4>克隆体必须剥掉 id</h4>
        <p>克隆时要 <code>querySelectorAll('[id]')</code> 全部移除 id，否则页面里会出现重复 id，锚点跳转立刻失效。</p>
        <h4>事件委托到 document</h4>
        <p>收起/展开按钮的监听是绑在 <code>document</code> 上的，而不是绑在按钮本身——这样克隆出来的按钮<b>不需要重新绑定</b>就能工作。</p>

        <h2>可以直接借鉴的设计点（含反面教材）</h2>
        <div class="card">
          <div class="table-box">
            <table>
              <thead><tr><th style="width:230px;">设计点</th><th>价值</th></tr></thead>
              <tbody>
                <tr><td class="k">Unicode slug</td><td>中文标题生成可读锚点（<code>topic-outline-效果</code>），而不是一串无意义字符</td></tr>
                <tr><td class="k">吸顶高度动态补偿</td><td>解决固定导航遮挡锚点目标的经典问题，<b>实测落点与 <code>scrollMarginTop</code> 精确一致</b></td></tr>
                <tr><td class="k">事件委托到 document</td><td>克隆体无需重新绑定事件即可复用交互</td></tr>
                <tr><td class="k">克制降级</td><td>标题 &lt; 2 不渲染、无 API 降级、移动端隐藏、尊重减弱动效</td></tr>
                <tr><td class="k"><b>窄带 IntersectionObserver</b></td><td><b>⚠️ 反面教材</b>：思路（窄带 + 免节流）本身没错，但<b>必须同时检查祖先是否有 <code>overflow:hidden</code> 裁剪</b>，否则在真实站点上会静默失效</td></tr>
                <tr><td class="k"><b>占位符 + 克隆停靠</b></td><td><b>⚠️ 需权衡</b>：能实现常驻悬浮，但<b>会把 <code>is-active</code> 一并克隆并导致状态分叉</b>；能不用克隆就不用</td></tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="note blue">
          一句话总结：<b>能把「降级」写清楚的前端组件，才是能上生产的前端组件</b>——这段代码几乎每个分支都考虑了「环境不支持时怎么办」。<br>
          但另一句话同样重要：<b>「写了兜底」不等于「链路真的通」</b>——这条滚动高亮有特性检测、有 observer、代码完全正确，却因为一个祖先样式而从未生效，且不报任何错。
        </div>

        <h2>在 Vue 里落地要注意什么</h2>
        <p>原实现是直接操作 DOM 的插件，搬到 Vue 组件里需要处理生命周期与销毁，否则会留下野监听器。</p>
        <h3>挂载与清理成对出现</h3>
        <p>所有 <code>IntersectionObserver</code>、<code>document</code> 事件监听、<code>window</code> 事件监听，都必须记录引用并在 <code>beforeDestroy</code> 里注销。</p>
        <h3>扫描源换成 ref</h3>
        <p>不再依赖站点专用的 <code>.post-content</code> 类名，而是通过 <code>ref</code> 直接拿到演示正文的根节点，扫描逻辑本身完全不变。</p>

        <h3>最大的坑：后台里滚动的不是 window</h3>
        <p>在独立页面里 <code>window.scrollTo()</code> 就能滚，但在本后台的布局中，真正的滚动容器是内容区 <code>.content-wrapper</code>（<code>overflow: auto</code>），<b><code>window</code> 本身根本不滚动</b>。</p>
        <h4>三个连带后果</h4>
        <p>① <code>window.scrollY</code> 恒为 0 → 点击目录算出的目标位置永远是错的；② <code>window.scrollTo()</code> 无任何效果 → 点了不动；③ <code>IntersectionObserver</code> 若用默认 viewport 作 root，窄带是按浏览器视口算的，与容器内实际可见范围不一致。</p>
        <div class="note warn">
          <b>正确做法：</b>向上回溯找到真正滚动（<code>overflow-y: auto/scroll</code> 且 <code>scrollHeight &gt; clientHeight</code>）的祖先作为滚动容器，滚动读写在它身上进行，并把该容器作为 <code>IntersectionObserver</code> 的 <code>root</code> 传入。
        </div>
        <h3>悬浮还要有「行程」</h3>
        <p><code>position: sticky</code> 要生效，除了指定 <code>top</code>，元素还必须在其滚动祖先内有<b>可滑动的空间</b>。如果父级是 <code>align-items: start</code> 的 grid 列，该列高度会被压成与卡片等高，<b>sticky 完全没有行程，滚动时纹丝不动</b>。</p>
      </article>

      <!-- 悬浮目录框：本模块的实现本体 -->
      <aside class="demo-sidebar">
        <div class="demo-sidebar-sticky">
          <section
            ref="outline"
            class="card sidebar-card topic-outline-card"
            :class="{ 'is-collapsed': collapsed }"
            data-topic-outline-card="1"
            aria-label="文章目录"
          >
            <div class="topic-outline-title">
              <span class="topic-outline-title-main">文章目录</span>
              <button
                class="topic-outline-toggle"
                type="button"
                :aria-expanded="String(!collapsed)"
                :aria-label="collapsed ? '展开文章目录' : '收起文章目录'"
                :title="collapsed ? '展开文章目录' : '收起文章目录'"
                @click="toggleCollapsed"
              >{{ collapsed ? '展开' : '收起' }}</button>
            </div>
            <ol v-show="!collapsed" class="topic-outline-list">
              <li v-for="item in headings" :key="item.id">
                <a
                  class="topic-outline-link"
                  :class="{ 'is-active': item.id === activeId }"
                  :data-topic-outline-level="item.level"
                  :href="`#${encodeURIComponent(item.id)}`"
                  :title="item.text"
                  @click.prevent="navigateTo(item)"
                >{{ item.text }}</a>
              </li>
            </ol>
          </section>
          <div class="demo-sidebar-hint">
            目录卡片由 <code>h2 / h3 / h4</code> 实时扫描生成，共 <b>{{ headingCount }}</b> 项
          </div>
        </div>
      </aside>
    </div>

    <!-- ========== 2 关键参数 ========== -->
    <h2><span class="num">2</span>关键参数速查</h2>
    <p class="lead">下面是原实现里几个「不看源码就猜不到」的数值，本模块全部保持一致。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:260px;">参数</th><th style="width:220px;">取值</th><th>作用</th></tr></thead>
          <tbody>
            <tr><td class="k">扫描选择器</td><td><code>h2, h3, h4</code></td><td>只取这三级，忽略空标题</td></tr>
            <tr><td class="k">最小标题数</td><td><code>2</code></td><td>少于 2 个直接不渲染目录</td></tr>
            <tr><td class="k">锚点前缀</td><td><code>topic-outline-</code></td><td>URL 上 <code>#topic-outline-xxx</code> 的来源</td></tr>
            <tr><td class="k">锚点长度上限</td><td><code>48</code></td><td>超长标题截断，避免 id 过长</td></tr>
            <tr><td class="k">高亮触发带</td><td><code>-10% 0px -75% 0px</code></td><td>把视口压成约 15% 高度的横带（<b>原站点上此链路实测失效</b>，见第 4 节）</td></tr>
            <tr><td class="k">滚动间距下限</td><td><code>12px</code></td><td><code>Math.max(12, 顶栏高 + 12)</code></td></tr>
            <tr><td class="k">卡片最大高度</td><td><code>calc(100vh - 92px)</code></td><td>标题过多时目录自己滚，不撑长侧栏</td></tr>
            <tr><td class="k">列表最大高度</td><td><code>calc(100vh - 170px)</code></td><td>超出后内部滚动</td></tr>
            <tr><td class="k">缩进</td><td>h3 <code>20px</code> / h4 <code>30px</code></td><td>用 <code>data-topic-outline-level</code> 属性选择器而非嵌套列表</td></tr>
            <tr><td class="k">移动端断点</td><td><code>≤720px</code> 隐藏</td><td>手机上不显示目录</td></tr>
            <tr><td class="k">卡片圆角 / 投影</td><td><code>6px</code> / <code>0 4px 14px rgba(16,24,40,.04)</code></td><td>实测值，极淡投影</td></tr>
            <tr><td class="k">标题字号</td><td>15px / 700</td><td>实测「文章目录」为纯黑粗体</td></tr>
            <tr><td class="k">选中项配色</td><td><code>rgb(51,65,85)</code> on <code>rgb(241,245,249)</code></td><td>实测 slate-700 / slate-100，比普通项更深</td></tr>
            <tr><td class="k">悬浮定位</td><td><code>top: calc(offset + 24px)</code></td><td>24px 让位给下方「返回顶部」按钮，实测恒为 y=85</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ========== 3 结构 ========== -->
    <h2><span class="num">3</span>DOM 结构与 CSS 思路</h2>
    <p class="lead">层级信息不靠嵌套列表表达，而是写在属性上，CSS 用属性选择器做缩进——这样列表永远是扁平的，插入和更新都简单。</p>
    <pre class="code"><code>section.card.sidebar-card.topic-outline-card[aria-label="文章目录"]
├── div.topic-outline-title
│   ├── span.topic-outline-title-main   → "文章目录"
│   └── button.topic-outline-toggle      → "收起" / "展开"
└── ol.topic-outline-list
    └── li > a.topic-outline-link[data-topic-outline-level="2|3|4"]</code></pre>
    <p class="lead">CSS 上有四个容易被忽略、但直接决定手感的属性：</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:250px;">属性</th><th>解决的问题</th></tr></thead>
          <tbody>
            <tr><td class="k">overscroll-behavior: contain</td><td>目录滚到底后<b>不会带动整页滚动</b>（滚动链隔离）</td></tr>
            <tr><td class="k">scrollbar-gutter: stable</td><td>滚动条出现/消失<b>不引起横向抖动</b></td></tr>
            <tr><td class="k">::before 做左侧竖条</td><td>选中态竖条用绝对定位伪元素，<b>不参与 padding 计算</b>，比 border-left 稳</td></tr>
            <tr><td class="k">max-height + overflow:auto</td><td>标题很多时<b>目录自己滚</b>，侧栏高度不被撑开</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="note">
      <b>为什么竖条不用 border-left？</b>因为 border 会挤占内容盒宽度，选中时文字会横向跳动一下。用 <code>position: absolute</code> 的 2px 伪元素，就完全不参与布局。
    </div>

    <!-- ========== 4 悬浮态实测与真实缺陷 ========== -->
    <h2><span class="num">4</span>悬浮态实测：原实现的一个真实缺陷</h2>
    <p class="lead">以下是<b>运行时实测</b>结论（视口 633×1265、文档高 1927px、<code>scrollY=700</code>），不是源码推断。它推翻了「窄带观察器在正常工作」这个想当然的判断。</p>

    <div class="note warn">
      <b>⚠️ 核心发现：原站点的「滚动时自动高亮」链路是坏的。</b>
      滚动位置从 0 → 400 → 800 → 1200 → 1600 全程，<code>.is-active</code> <b>始终停在第一项</b>，从不随滚动切换。
      此前看到的「安装」「源码」等项高亮，全部是<b>点击目录项时被手动设置后的残留状态</b>，与 IntersectionObserver 无关。
    </div>

    <h3>验证过程：排除了「参数没调好」的可能</h3>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:330px;">实验</th><th>结果</th></tr></thead>
          <tbody>
            <tr><td class="k">用站点同款参数重建 observer</td><td>回调数 = <b>0</b>（即使目标标题实测 <code>inBand: true</code>，确实位于观察带内）</td></tr>
            <tr><td class="k">换成 <code>rootMargin: '0px 0px 0px 0px'</code></td><td>仍 <b>0 命中</b></td></tr>
            <tr><td class="k">换成 <code>-10% 0px -25% 0px</code></td><td>仍 <b>0 命中</b></td></tr>
            <tr><td class="k"><b>判定实验</b>：手动清空全部 <code>is-active</code> 后普通滚动</td><td>高亮<b>再也不会自动恢复</b> → 证明该链路从未工作</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <h3>根因：被祖先元素裁剪，而不是 root 的问题</h3>
    <p class="lead">祖先链实测如下——「长文折叠」模块把正文包进了一个 <code>overflow:hidden</code> 且仅 560px 高的容器，而内部真实内容高达 13313px：</p>
    <pre class="code"><code>H2.topic-outline-anchor
└── DIV.nb-editor-post-content              (h = 13313)
    └── DIV.post-readability-content        (h = 13313)
        └── DIV.long-content-fold-content
              .long-content-fold-is-collapsed   ← overflow:hidden, h = 560  ★
            └── DIV.post-content</code></pre>
    <div class="note">
      除首个标题外，所有标题都被<b>裁剪到可视区之外</b>，浏览器判定其不可见，<code>IntersectionObserver</code> 因此永不回调 —— <b>无论 rootMargin 怎么调都无效</b>，因为裁剪来自<b>祖先元素</b>，而不是观察根（root）。这是「调参调不好」类问题的经典陷阱。
    </div>

    <h3>对照：点击跳转这条链路完全正常</h3>
    <p class="lead">同一套代码里，<code>topic-outline-navigate</code> 监听（点击目录时自动展开折叠）工作得非常好。点击「安装」后实测：</p>
    <pre class="code"><code>foldClass : long-content-fold-content   ← 折叠类被移除
foldH     : 13313px                     ← 从 560px 展开到 13313px
headingY  : 61                          ← 恰好等于 scrollMarginTop:61px，吸顶补偿精确
active    : ["安装"]                     ← 高亮正确切换
url.hash  : #topic-outline-%E5%AE%89%E8%A3%85</code></pre>
    <div class="note blue">
      即：<b>点击目录项 → 自动展开折叠 → 精确滚动 → 高亮正确</b>这条链路完全正常；<b>唯独「滚动时自动高亮」这条是坏的</b>。
    </div>

    <h3>推测原因：初始化竞态</h3>
    <p class="lead">该模块与「长文折叠」模块存在初始化竞态 —— observer 绑定 <code>headings</code> 时（<code>run-at document-idle</code>），标题已处于折叠容器内，注册即为「不交叉」状态；此后即便展开，<code>IntersectionObserver</code> 只在交叉状态<b>变化</b>时回调，元素始终被判为未交叉时不会有新回调，需重新 <code>observe()</code> 或强制一次状态翻转才能恢复。</p>

    <h3>本模块的取舍</h3>
    <p class="lead">本页演示没有「长文折叠」容器，所以窄带高亮<b>可以正常工作</b>（你可以滚动上方演示区验证）。但必须说清楚：这是<b>演示环境比原站点干净</b>，不代表原实现没问题。真实站点上，这条链路是失效的。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:230px;">方面</th><th style="width:280px;">原始实现</th><th>本模块</th></tr></thead>
          <tbody>
            <tr><td class="k">运行形态</td><td>自研插件 <code>topic_outline_main()</code>，全局注入</td><td>Vue 单文件组件，随路由加载</td></tr>
            <tr><td class="k">扫描目标</td><td><code>.topic-post-list .post-entry:first-child .post-content</code></td><td>组件内 <code>ref</code> 指向的演示正文</td></tr>
            <tr><td class="k">挂载点</td><td><code>sidebar.prepend(card)</code></td><td>组件模板内直接渲染</td></tr>
            <tr><td class="k">事件绑定</td><td>委托到 <code>document</code></td><td>模板 <code>@click</code>，由 Vue 管理</td></tr>
            <tr><td class="k">收起状态</td><td>DOM 类 + 全局按钮同步</td><td>响应式 <code>collapsed</code> 数据驱动</td></tr>
            <tr><td class="k">克隆停靠</td><td>克隆到 dock + <code>position: fixed</code>；<b>高亮状态会被一并克隆且此后不再同步</b></td><td>容器 <code>position: sticky</code>，<b>无克隆即无所说的状态分叉</b></td></tr>
            <tr><td class="k">滚动高亮</td><td><b>实测失效</b>（祖先 overflow 裁剪）</td><td>无折叠容器，工作正常</td></tr>
            <tr><td class="k">销毁清理</td><td>无需（整页刷新）</td><td><code>beforeDestroy</code> 中注销观察器与监听</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <h3>实测确认的其他行为</h3>
    <div class="card">
      <ul class="list">
        <li><b>点击跳转精准</b>：落点 <code>headingY=61px</code> 与 <code>scrollMarginTop=61px</code> 完全一致，吸顶补偿公式 <code>max(12, topBar高度 + 12)</code> 正确。</li>
        <li><b>收起按钮正常</b>：切换 <code>.is-collapsed</code>，<code>aria-expanded</code> 在 <code>true/false</code> 间正确翻转，<code>aria-label</code> 同步在「收起文章目录 / 展开文章目录」间切换。</li>
        <li><b><code>is-active</code> 会被克隆复制</b>：克隆发生在高亮设置之后，类名被一并复制，此后<b>两份高亮状态不再同步</b> —— 这解释了悬浮框的高亮为何「冻结在克隆那一刻」。</li>
      </ul>
    </div>

    <div class="note blue">
      <b>关于停靠方案：</b>原站点用「克隆 + fixed」是因为它的侧栏会被别的脚本不断改动位置。在当前后台里，外层容器用 <code>position: sticky</code> 就能达到完全相同的观感，而且顺带规避了上面这条<b>克隆导致的高亮状态分叉</b>。
    </div>

    <!-- ========== 4.1 悬浮定位公式 ========== -->
    <h3>悬浮定位公式（实测值）</h3>
    <p class="lead">侧栏上由 JS 写入的三个 CSS 变量实测为 <code>--back-to-top-sidebar-offset: 61px</code>（顶栏高 + 12）、<code>left: 965.5px</code>、<code>width: 230px</code>，克隆体最终生效：</p>
    <pre class="code"><code>position: fixed;
top:   85px;       /* = 61px(--offset) + 24px(额外让位给「返回顶部」按钮) */
left:  965.5px;
width: 230px;
z-index: 3;</code></pre>
    <div class="note">
      内联样式确认了那 24px 的来源：<code>clone.style.top === "calc(var(--back-to-top-sidebar-offset) + 24px)"</code>。<br>
      <b>全程滚动实测</b>（scrollY 0 → 400 → 800 → 1200 → 1600）：克隆体 <code>y</code> <b>恒为 85</b>、高度恒为 204 —— 这是「悬浮」的直接证据。
    </div>

    <!-- ========== 5 核心代码 ========== -->
    <h2><span class="num">5</span>核心代码</h2>
    <p class="lead">实现本体只有几十行，下面是最关键的三个片段。</p>

    <h3>扫描与锚点生成</h3>
    <pre class="code"><code>// Unicode 感知：中文标题不会被清空
const slug = (text, index) => {
  const base = text.toLowerCase().trim()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || `section-${index + 1}`
  let id = `topic-outline-${base}`
  let suffix = 2
  while (used.has(id)) id = `topic-outline-${base}-${suffix++}`
  used.add(id)
  return id
}

// 尊重标题已有 id，不覆盖
if (!heading.id) heading.id = slug(heading.textContent, index)</code></pre>

    <h3>窄带高亮</h3>
    <pre class="code"><code>const observer = new IntersectionObserver(entries => {
  const visible = entries
    .filter(entry => entry.isIntersecting)
    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
  if (!visible) return
  this.activeId = visible.target.id
}, { rootMargin: '-10% 0px -75% 0px' })

headings.forEach(heading => observer.observe(heading))</code></pre>

    <h3>跳转与吸顶补偿</h3>
    <pre class="code"><code>navigateTo(item) {
  const heading = document.getElementById(item.id)
  if (!heading) return
  const offset = this.scrollOffset()
  const top = window.scrollY + heading.getBoundingClientRect().top - offset
  window.scrollTo({
    top: Math.max(0, top),
    // 尊重 prefers-reduced-motion
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
  })
  history.replaceState(null, '', `#${encodeURIComponent(heading.id)}`)
}</code></pre>

    <footer>
      本页演示的目录卡片为真实运行效果，标题列表由正文的 h2/h3/h4 实时扫描生成；<br>
      模块路由：<code>/custom-component/floating-table-box</code> ｜ 组件：<code>frontend/src/views/custom-component/floating-table-box/index.vue</code>
    </footer>
  </div>
</template>

<script>
/**
 * 悬浮目录框（文章目录模块）
 *
 * 还原自 linux.sb 主题页的「文章目录」卡片（plugins.js 中的 topic_outline 插件）。
 * 原实现直接操作 DOM 并注入侧栏；这里 Vue 化后：
 *   1. 扫描目标由组件内的 ref 提供（不再依赖站点专用类名）；
 *   2. 交互交由 Vue 响应式驱动，同时保留原实现全部行为细节；
 *   3. IntersectionObserver 与事件监听在 beforeDestroy 中统一注销。
 *
 * 与原始实现保持一致的关键规格：
 *   - 只取 h2/h3/h4，忽略空标题；标题少于 2 个不渲染目录；
 *   - 锚点前缀 topic-outline-，Unicode 感知（中文保留），冲突递增后缀，不覆盖已有 id；
 *   - 高亮触发带 rootMargin: '-10% 0px -75% 0px'；
 *   - 跳转做吸顶高度补偿，并尊重 prefers-reduced-motion。
 *
 * 注意（运行时实测结论，详见页面第 4 节）：
 *   原站点上「滚动时自动高亮」这条链路实际是失效的 —— 「长文折叠」模块把正文包进
 *   overflow:hidden 且仅 560px 高的容器，导致除首个标题外的所有标题被裁剪到可视区外，
 *   IntersectionObserver 因此永不回调，调整 rootMargin 无效（裁剪来自祖先而非 root）。
 *   本页演示没有该折叠容器，所以窄带高亮可以正常工作；但这属于演示环境比原站点干净，
 *   并不代表原实现没有问题。
 */
export default {
  name: 'FloatingTableBox',

  data() {
    return {
      /** 扫描到的标题列表：{ id, text, level } */
      headings: [],
      /** 当前高亮的标题 id */
      activeId: '',
      /** 目录是否收起 */
      collapsed: false,
      /** 是否处于悬浮停靠状态（用于演示状态展示） */
      docked: false,
      /** IntersectionObserver 实例，销毁时需要 disconnect */
      observer: null,
      /** 真正的滚动容器（window 或 .content-wrapper 等元素） */
      scrollTarget: null,
      /** 滚动监听节流标记 */
      scrollTicking: false
    }
  },

  computed: {
    headingCount() {
      return this.headings.length
    },

    activeText() {
      const hit = this.headings.find(item => item.id === this.activeId)
      return hit ? hit.text : '—'
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.buildOutline()
      this.bindDockDetect()
    })
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.scrollTarget) {
      this.scrollTarget.removeEventListener('scroll', this.onScroll)
      this.scrollTarget = null
    }
    window.removeEventListener('resize', this.updateAnchorOffset)
    window.removeEventListener('resize', this.onScroll)
  },

  methods: {
    /**
     * 扫描正文标题并生成目录数据。
     * 与原始实现一致：只取 h2/h3/h4、忽略空标题、少于 2 个不渲染。
     */
    buildOutline() {
      const article = this.$refs.article
      if (!article) return

      const rawHeadings = Array.from(article.querySelectorAll('h2,h3,h4'))
        .filter(item => item.textContent.trim() !== '')

      // 标题少于 2 个时不渲染目录（原实现的克制降级）
      if (rawHeadings.length < 2) {
        this.headings = []
        return
      }

      // 收集全站已有 id，避免生成的锚点撞车
      const used = new Set(
        Array.from(document.querySelectorAll('[id]')).map(item => item.id)
      )

      const slug = (text, index) => {
        const base = text
          .toLowerCase()
          .trim()
          // \p{L}\p{N} + u 标志：Unicode 感知，中文标题不会被清空
          .replace(/[^\p{L}\p{N}]+/gu, '-')
          .replace(/^-|-$/g, '')
          .slice(0, 48) || `section-${index + 1}`

        let id = `topic-outline-${base}`
        let suffix = 2
        while (used.has(id)) {
          id = `topic-outline-${base}-${suffix++}`
        }
        used.add(id)
        return id
      }

      this.headings = rawHeadings.map((heading, index) => {
        // 不覆盖标题已有的 id
        if (!heading.id) {
          heading.id = slug(heading.textContent, index)
        }
        heading.classList.add('topic-outline-anchor')
        return {
          id: heading.id,
          text: heading.textContent.trim(),
          level: heading.tagName.slice(1)
        }
      })

      this.updateAnchorOffset()
      window.addEventListener('resize', this.updateAnchorOffset)

      // 首项先点亮
      this.activeId = this.headings[0].id
      this.initObserver(rawHeadings)
    },

    /**
     * 窄带 IntersectionObserver 高亮。
     * rootMargin 把有效视口压成约 15% 高度的横带，比 scroll 监听省性能且无需节流。
     *
     * 注意 root 必须传真正的滚动容器：后台里滚动的是 .content-wrapper，
     * 若用默认的 viewport 作 root，窄带会按浏览器视口计算，与容器内实际可见范围不一致。
     */
    initObserver(rawHeadings) {
      if (!('IntersectionObserver' in window)) {
        // 特性检测兜底：不支持则只保留静态目录，不报错
        return
      }

      const container = this.getScrollContainer()
      const root = container === window ? null : container

      this.observer = new IntersectionObserver(entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (!visible) return
        this.activeId = visible.target.id
      }, { root, rootMargin: '-10% 0px -75% 0px' })

      rawHeadings.forEach(heading => this.observer.observe(heading))
    },

    /**
     * 找到真正在滚动的祖先容器。
     * 后台布局里 .content-wrapper 才是滚动容器（overflow:auto），window 并不滚动，
     * 因此不能直接对 window 做 scrollTo / scrollY。
     */
    getScrollContainer() {
      let node = this.$el && this.$el.parentElement
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node)
        const overflowY = style.overflowY
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
          return node
        }
        node = node.parentElement
      }
      return window
    },

    /**
     * 读取当前滚动量（兼容 window 与普通元素容器）。
     */
    readScrollTop(container) {
      return container === window
        ? window.scrollY || window.pageYOffset || 0
        : container.scrollTop
    },

    /**
     * 计算点击跳转时需要预留的顶部间距（吸顶高度补偿）。
     */
    scrollOffset() {
      const topBar = document.querySelector('.top')
      const topFixed = topBar ? getComputedStyle(topBar).position === 'fixed' : false
      const topHeight = topFixed && topBar
        ? Math.ceil(topBar.getBoundingClientRect().height)
        : 0
      return Math.max(12, topHeight + (topFixed ? 12 : 0))
    },

    /**
     * 给每个标题设置 scrollMarginTop，保证原生锚点跳转（如 URL 带 hash 进入）
     * 也不会被固定顶部导航遮挡。
     */
    updateAnchorOffset() {
      const offset = this.scrollOffset()
      this.headings.forEach(item => {
        const heading = document.getElementById(item.id)
        if (heading) {
          heading.style.scrollMarginTop = `${offset}px`
        }
      })
    },

    /**
     * 点击目录项：手动计算滚动位置，并同步地址栏 hash。
     * 注意滚动的是祖先容器（.content-wrapper）而不是 window。
     */
    navigateTo(item) {
      const heading = document.getElementById(item.id)
      if (!heading) return

      this.activeId = item.id
      const container = this.getScrollContainer()
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      // 相对滚动容器计算目标位置（容器自身可能也有偏移）
      let top
      if (container === window) {
        top = window.scrollY + heading.getBoundingClientRect().top - this.scrollOffset()
      } else {
        const containerRect = container.getBoundingClientRect()
        top = container.scrollTop
          + (heading.getBoundingClientRect().top - containerRect.top)
          - this.scrollOffset()
      }
      top = Math.max(0, top)

      if (container === window) {
        window.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' })
      } else if (reduced) {
        container.scrollTop = top
      } else {
        container.scrollTo({ top, behavior: 'smooth' })
      }

      history.replaceState(null, '', `#${encodeURIComponent(heading.id)}`)
    },

    toggleCollapsed() {
      this.collapsed = !this.collapsed
    },

    /**
     * 检测目录卡片是否已随滚动贴住顶部（悬浮生效）。
     * 监听对象必须是真正的滚动容器，而不是 window。
     */
    bindDockDetect() {
      this.scrollTarget = this.getScrollContainer()
      this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true })
      window.addEventListener('resize', this.onScroll)
      this.onScroll()
    },

    onScroll() {
      if (this.scrollTicking) return
      this.scrollTicking = true
      window.requestAnimationFrame(() => {
        const box = this.$refs.outline
        if (box) {
          // 卡片顶部贴到（或越过）容器顶部时，视为已悬浮停靠
          const container = this.scrollTarget
          const baseTop = container === window
            ? 0
            : container.getBoundingClientRect().top
          this.docked = box.getBoundingClientRect().top <= baseTop + 1
        }
        this.scrollTicking = false
      })
    }
  }
}
</script>

<style scoped>
  /* ==================== 页面基础布局（与平台内其它说明页保持一致） ==================== */
  .wrap {
    /* 撑满内容区（居中限宽时左右留白由 margin 提供，内边距由内容区统一提供） */
    height: 100%;
    max-width: 1080px;
    margin: 0 auto;
    padding: 12px;
    background: var(--surface-1);
    color: #1a1d21;
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    line-height: 1.7;
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
    overflow: auto;
    border-radius: 8px;
  }
  .wrap * {
    box-sizing: border-box;
  }
  .wrap code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    background: var(--surface-2);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 12.5px;
  }
  header {
    margin-bottom: 20px;
    padding-bottom: 24px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  }
  h1 {
    font-size: 26px;
    font-weight: 700;
    margin: 0 0 8px;
    letter-spacing: -0.4px;
    color: #1a1d21;
  }
  .sub {
    color: #5a6270;
    font-size: 14px;
  }
  .meta {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .chip {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 20px;
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    color: #5a6270;
  }

  h2 {
    font-size: 19px;
    font-weight: 650;
    margin: 40px 0 6px;
    display: flex;
    align-items: center;
    gap: 10px;
    letter-spacing: -0.2px;
    color: #1a1d21;
  }
  h2 .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: #eef3fe;
    color: #2f6fed;
    font-size: 13px;
    font-weight: 700;
    flex: none;
  }
  h3 {
    font-size: 15px;
    font-weight: 650;
    margin: 22px 0 6px;
    color: #1a1d21;
  }
  .lead {
    color: #5a6270;
    margin: 0 0 16px;
    font-size: 13.5px;
  }
  .lead:last-child {
    margin-bottom: 0;
  }

  .card {
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 16px;
  }
  .table-box {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }
  thead th {
    background: var(--surface-1);
    text-align: left;
    font-weight: 600;
    color: #5a6270;
    padding: 10px 14px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    white-space: nowrap;
    font-size: 12.5px;
  }
  tbody td {
    padding: 11px 14px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    vertical-align: top;
    color: #3a414c;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tbody tr:hover {
    background: var(--surface-1);
  }
  td.k {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    color: #1a1d21;
  }

  .list {
    margin: 0;
    padding: 14px 20px 14px 34px;
    font-size: 13.5px;
    color: #3a414c;
  }
  .list li {
    margin: 5px 0;
  }

  .code {
    background: #1e2229;
    color: #e6e8eb;
    border-radius: 10px;
    padding: 14px 16px;
    margin: 0 0 16px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    line-height: 1.65;
    white-space: pre;
  }
  .code code {
    background: transparent;
    padding: 0;
    color: inherit;
    font-size: inherit;
  }

  .note {
    background: #fffdf5;
    border: 1px solid #f0e2c0;
    border-left: 3px solid #e0a800;
    border-radius: 0 8px 8px 0;
    padding: 12px 16px;
    font-size: 13px;
    color: #5c4a15;
    margin-bottom: 16px;
  }
  .note b {
    color: #3d3110;
  }
  .note.blue {
    background: #eef3fe;
    border-color: #c9dbfb;
    border-left-color: #2f6fed;
    color: #1e3d73;
  }
  .note.blue b {
    color: #14305c;
  }
  .note.warn {
    background: #fdecec;
    border-color: #f7cdcd;
    border-left-color: #d93a3a;
    color: #7d2626;
  }
  .note.warn b {
    color: #5c1a1a;
  }
  .note p {
    margin: 0 0 8px;
  }
  .note p:last-child {
    margin-bottom: 0;
  }

  .flow {
    display: flex;
    flex-direction: column;
    margin: 0 0 16px;
  }
  .step {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 14px 16px;
    border: 1px solid rgba(31, 45, 61, 0.06);
    background: var(--surface-1);
  }
  .step:first-child {
    border-radius: 12px 12px 0 0;
  }
  .step:last-child {
    border-radius: 0 0 12px 12px;
  }
  .step:only-child {
    border-radius: 12px;
  }
  .step + .step {
    border-top: none;
  }
  .step .q {
    font-weight: 600;
    min-width: 200px;
    flex: none;
    font-size: 13.5px;
    color: #1a1d21;
  }
  .step .a {
    color: #5a6270;
    font-size: 13px;
  }

  footer {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid rgba(31, 45, 61, 0.06);
    color: #8b939f;
    font-size: 12.5px;
    line-height: 1.8;
  }

  /* ==================== 演示区：正文 + 悬浮目录框 ==================== */
  .demo-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 14px;
    padding: 12px 16px;
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    border-radius: 12px;
  }
  .demo-tip {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    font-size: 12.5px;
    color: #5a6270;
  }
  .demo-tip b {
    color: #1a1d21;
  }
  .demo-stat {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .stat-pill {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 20px;
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    color: #5a6270;
    white-space: nowrap;
  }
  .stat-pill b {
    color: #1a1d21;
  }
  .stat-pill.on {
    background: #eef3fe;
    border-color: #c9dbfb;
    color: #2f6fed;
  }
  .stat-pill.on b {
    color: #2f6fed;
  }

  .demo-stage {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 20px;
    /* 不要用 align-items:start：那会让侧栏列只有卡片自身的高度，
       sticky 元素随之失去可滑动行程，滚动时无法悬浮。默认 stretch 才能让
       侧栏列与正文列等高，目录框在列内滑动并贴顶悬浮。 */
    align-items: stretch;
    margin-bottom: 8px;
  }

  .demo-article {
    background: var(--surface-1);
    border: 1px solid rgba(31, 45, 61, 0.06);
    border-radius: 12px;
    padding: 20px 22px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.03);
  }
  .demo-article-head {
    padding-bottom: 14px;
    margin-bottom: 6px;
    border-bottom: 1px dashed #e6e8eb;
  }
  .demo-article-title {
    font-size: 17px;
    font-weight: 700;
    color: #1a1d21;
    letter-spacing: -0.2px;
  }
  .demo-article-sub {
    margin-top: 4px;
    font-size: 12.5px;
    color: #8b939f;
  }
  .demo-article h2 {
    font-size: 16px;
    font-weight: 650;
    margin: 26px 0 8px;
    color: #1a1d21;
    display: block;
  }
  .demo-article h3 {
    font-size: 14.5px;
    font-weight: 650;
    margin: 20px 0 6px;
    color: #1a1d21;
  }
  .demo-article h4 {
    font-size: 13.5px;
    font-weight: 600;
    margin: 16px 0 4px;
    color: #3a414c;
  }
  .demo-article p {
    margin: 0 0 10px;
    font-size: 13.5px;
    color: #3a414c;
  }

  /* 目录卡片吸顶容器 */
  .demo-sidebar {
    position: relative;
    /* 关键：让侧栏列的高度与正文列一致（grid 项默认 stretch，
       但若被 align-items:start 压成内容高，sticky 就没有可滑动的行程） */
    align-self: stretch;
    height: 100%;
  }
  .demo-sidebar-sticky {
    position: sticky;
    /* 相对滚动容器（.content-wrapper）顶部留出的间距 */
    top: 12px;
    /* 保证卡片自身可滚动时 sticky 仍生效 */
    max-height: calc(100vh - 100px);
  }
  .demo-sidebar-hint {
    margin-top: 10px;
    font-size: 12px;
    color: #8b939f;
    line-height: 1.6;
  }
  .demo-sidebar-hint b {
    color: #5a6270;
  }

  /* ==================== 目录卡片本体（还原 linux.sb topic_outline 规格） ==================== */
  .topic-outline-card {
    display: grid;
    gap: 11px;
    align-content: start;
    max-height: calc(100vh - 92px);
    padding: 14px;
    overflow: hidden;
    margin-bottom: 0;
  }
  .topic-outline-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: 0;
    padding: 1px 2px 9px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    color: #1a1d21;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.35;
  }
  .topic-outline-title-main {
    min-width: 0;
  }
  .topic-outline-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    min-height: 26px;
    margin: -4px -4px 0 0;
    padding: 3px 7px;
    border: 1px solid rgba(31, 45, 61, 0.06);
    border-radius: 999px;
    background: var(--surface-1);
    color: #5a6270;
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;
  }
  .topic-outline-toggle:hover {
    border-color: #2f6fed;
    background: #eef3fe;
    color: #2f6fed;
  }
  .topic-outline-toggle:focus-visible {
    outline: 0;
    border-color: #2f6fed;
    box-shadow: 0 0 0 2px rgba(47, 111, 237, 0.25);
  }
  .topic-outline-list {
    display: grid;
    gap: 2px;
    min-height: 0;
    max-height: calc(100vh - 170px);
    margin: 0;
    padding: 0 5px 1px 0;
    overflow: auto;
    /* 目录滚到底不带动整页滚动：滚动链隔离 */
    overscroll-behavior: contain;
    /* 滚动条出现/消失不引起横向抖动 */
    scrollbar-gutter: stable;
    list-style: none;
    scrollbar-width: auto;
    scrollbar-color: #2f6fed #f0f1f3;
  }
  .topic-outline-list::-webkit-scrollbar {
    width: 8px;
  }
  .topic-outline-list::-webkit-scrollbar-track {
    border-radius: 4px;
    background: var(--surface-2);
  }
  .topic-outline-list::-webkit-scrollbar-thumb {
    min-height: 32px;
    border: 2px solid #f0f1f3;
    border-radius: 6px;
    background: #d7dbe0;
  }
  .topic-outline-list::-webkit-scrollbar-thumb:hover {
    background: #2f6fed;
  }
  .topic-outline-link {
    position: relative;
    display: block;
    padding: 6px 8px 6px 10px;
    border-radius: 7px;
    color: #5a6270;
    font-size: 13px;
    line-height: 1.45;
    text-decoration: none;
    overflow-wrap: anywhere;
    transition: background 0.18s ease, color 0.18s ease, padding 0.18s ease;
  }
  /* 选中态左侧竖条：用伪元素而非 border-left，避免影响 padding 计算 */
  .topic-outline-link::before {
    position: absolute;
    top: 10px;
    bottom: 10px;
    left: 3px;
    width: 2px;
    border-radius: 2px;
    background: transparent;
    content: "";
  }
  .topic-outline-link:hover {
    background: #eef3fe;
    color: #2f6fed;
  }
  .topic-outline-link:focus-visible {
    outline: 0;
    background: #eef3fe;
    color: #2f6fed;
    box-shadow: 0 0 0 2px rgba(47, 111, 237, 0.25);
  }
  /* 层级缩进靠属性选择器，列表保持扁平 */
  .topic-outline-link[data-topic-outline-level="3"] {
    padding-left: 20px;
    font-size: 12.5px;
  }
  .topic-outline-link[data-topic-outline-level="4"] {
    padding-left: 30px;
    font-size: 12.5px;
    color: #8b939f;
  }
  .topic-outline-link.is-active {
    background: #eef3fe;
    color: #2f6fed;
    font-weight: 600;
  }
  .topic-outline-link.is-active::before {
    background: #2f6fed;
  }
  .topic-outline-card.is-collapsed {
    gap: 0;
  }
  .topic-outline-card.is-collapsed .topic-outline-title {
    padding-bottom: 1px;
    border-bottom-color: transparent;
  }
  .topic-outline-card.is-collapsed .topic-outline-list {
    display: none;
  }

  @media (max-width: 900px) {
    .demo-stage {
      grid-template-columns: minmax(0, 1fr);
    }
    .demo-sidebar-sticky {
      position: static;
    }
    .topic-outline-card {
      max-height: none;
    }
    .topic-outline-list {
      max-height: 320px;
    }
  }

  /* 原实现：≤720px 直接隐藏目录卡片 */
  @media (max-width: 720px) {
    .topic-outline-card {
      display: none;
    }
  }

  @media (max-width: 640px) {
    .wrap {
      padding: 16px;
    }
    .step {
      flex-direction: column;
      gap: 3px;
    }
    .step .q {
      min-width: 0;
    }
    h1 {
      font-size: 22px;
    }
    h2 {
      font-size: 17px;
    }
  }
</style>
